/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IButtonProps, IButtonStyles, IconButton } from '@fluentui/react/lib/Button';
import { FocusZone } from '@fluentui/react/lib/FocusZone';
import {
    CollapseAllVisibility,
    GroupedList,
    IGroupHeaderProps,
    IGroupHeaderStyleProps,
    IGroupHeaderStyles
} from '@fluentui/react/lib/GroupedList';
import { IIconStyleProps, IIconStyles } from '@fluentui/react/lib/Icon';
import { Stack } from '@fluentui/react/lib/Stack';
import { classNamesFunction, getRTLSafeKeyCode, KeyCodes, SelectionMode } from '@fluentui/react/lib/Utilities';
import * as React from 'react';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { TestPaneEntityPrediction } from 'src/luis/projects/testPane/components/common/TestPaneEntityPrediction';
import { UtteranceCompositeEntityStore } from 'src/luis/projects/testPane/types/types';
import { generateTestPaneEntityTree } from 'src/luis/projects/testPane/utils/testPaneEntityTreeGenerator';

export type TestPaneEntityTreeProps = {
    /**
     * The n-depth entity.
     */
    entity: UtteranceCompositeEntityStore;

    /**
     * Indicator whether the current tree is displayed
     * with required features disabled/enabled.
     */
    isSuppressionDisabled: boolean;

    /**
     * An array of the already expanded entity trees.
     */
    expandedIds: string[];

    /**
     * A callback to notify an expanding state change for the given id.
     */
    onExpandingChange: (id: string) => void;
};

const treeTheme = getScopedTheme('TestPaneEntityTree');
const expandingButtonStyles = classNamesFunction<IButtonProps, IButtonStyles>()({
    root: { width: 'auto', padding: 0 },
    rootHovered: { backgroundColor: 'transparent' }
});

const groupHeaderStyles = classNamesFunction<IGroupHeaderStyleProps, IGroupHeaderStyles>()({
    expand: { display: 'none' },
    groupHeaderContainer: { height: 'auto', alignItems: 'flex-start' },
    root: { backgroundColor: 'transparent', selectors: { '&:hover': { backgroundColor: 'transparent' } } }
});

/**
 * Displays the entity tree in the test pane.
 */
export const TestPaneEntityTree = (props: TestPaneEntityTreeProps) => {
    const { entity, isSuppressionDisabled, expandedIds, onExpandingChange } = props;
    const groups = React.useMemo(() => [generateTestPaneEntityTree(entity, expandedIds, !isSuppressionDisabled)], [entity, expandedIds]);

    /**
     * Displays the item of the tree.
     */
    const onRenderTitle = (item: IGroupHeaderProps) => {
        const hasChildren = item.group.children.length !== 0;
        const itemPadding = (item.group.level + 1) * 10 + (hasChildren ? 0 : 18);
        const entityToRender: UtteranceCompositeEntityStore = item.group.data.entity;
        const expandingIconStyles = classNamesFunction<IIconStyleProps, IIconStyles>()({
            root: {
                margin: 0,
                fontSize: '10px',
                lineHeight: '16px',
                color: entityToRender.parent.isSuppressed ? treeTheme.highlightedTextColor : treeTheme.expandingButtonDefaultColor
            }
        });

        return (
            <Stack
                horizontal
                tokens={{ padding: `0 0 0 ${itemPadding}px`, childrenGap: 8 }}
                styles={{
                    root: {
                        selectors: {
                            '&:hover': { cursor: hasChildren ? 'pointer' : 'auto' }
                        }
                    }
                }}>
                {hasChildren && (
                    <IconButton
                        styles={expandingButtonStyles}
                        iconProps={{ styles: expandingIconStyles, iconName: item.group.isCollapsed ? 'ChevronRight' : 'ChevronUp' }}
                    />
                )}

                <TestPaneEntityPrediction
                    utteranceSubtext={entityToRender.parent.text}
                    isHighlighted={entityToRender.parent.isSuppressed}
                    entityName={entityToRender.parent.utteranceEntity.entity.name}
                />
            </Stack>
        );
    };

    return (
        <FocusZone isInnerZoneKeystroke={e => e.which === getRTLSafeKeyCode(KeyCodes.right)}>
            <GroupedList
                compact
                items={[]}
                onRenderCell={null}
                groups={[...groups]}
                onShouldVirtualize={() => false}
                selectionMode={SelectionMode.none}
                groupProps={{
                    showEmptyGroups: true,
                    collapseAllVisibility: CollapseAllVisibility.hidden,
                    headerProps: {
                        indentWidth: 0,
                        styles: groupHeaderStyles,
                        onRenderTitle,
                        onGroupHeaderClick: group => onExpandingChange(group.key)
                    }
                }}
            />
        </FocusZone>
    );
};
